/**
 * Surface descriptors, client side (`MCK-20260812-006` §E).
 *
 * What a list endpoint filters on, what values each field takes, and where the values that are
 * not an enum come from — read from the server rather than restated here. That is the whole
 * point of the endpoint: a filter picker that carried its own list of fields would be the
 * second copy the amendment warns about, and the second copy is always the wrong one.
 *
 * **Reference data in the strongest sense §G has.** It is a property of the release, identical
 * for every caller in every workspace, and it changes on a deploy. One fetch per session,
 * shared through the cache, revalidated by an `ETag` the server computes from the answer
 * itself.
 *
 * **This module knows nothing about tasks or drawings.** It reads descriptors; what a field
 * *means* on a particular screen — which piece of that screen's filter state it binds to, what
 * a person should see it called — is that screen's business and lives beside it.
 */

import { queryOptions, useQuery } from '@tanstack/react-query';

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/openapi';

export type SurfaceDescriptor = components['schemas']['SurfaceDescriptor'];
export type SurfaceField = components['schemas']['SurfaceField'];

/**
 * The surfaces this frontend asks about, by the name their route claims.
 *
 * A union rather than a bare `string` so a typo is a compile error instead of a `404` nobody
 * sees until the rail is empty. It names *which endpoints we describe*, never what they
 * contain — adding a filter to either one changes what the panel offers with no edit here.
 */
export type SurfaceName =
  | 'project-tasks'
  | 'project-drawings'
  | 'project-reviews'
  | 'project-register';

export function surfaceQueryOptions(surface: SurfaceName) {
  return queryOptions({
    queryKey: ['surface-descriptor', surface] as const,
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/surfaces/{surface}/descriptor', {
          params: { path: { surface } },
        }),
      ),
    // A release's worth. The server's `Cache-Control` says the same thing to the browser; this
    // is what stops a second component mounting from asking again inside one session.
    staleTime: 24 * 60 * 60_000,
  });
}

export function useSurface(surface: SurfaceName) {
  return useQuery(surfaceQueryOptions(surface));
}

/**
 * The fields a picker can draw as a list of choices.
 *
 * **Enumerable is the whole test**, and it is a property of the field rather than a list kept
 * anywhere: a closed set of values (`values`), project data with an endpoint behind it
 * (`values_from`), or the hierarchy (`node`, which has a tree behind it). A free date or an
 * arbitrary string is none of those, and a picker that offered one would be asking somebody to
 * guess — which is the failure `KLQ-20260820-005` §6 means by *"an attribute a surface cannot
 * filter on cannot be offered at all"*, one step further in.
 */
export function offerableAxes(
  descriptor: SurfaceDescriptor | undefined,
): readonly SurfaceField[] {
  return (descriptor?.filters ?? []).filter(
    (one) =>
      one.type === 'node' ||
      (one.values?.length ?? 0) > 0 ||
      (one.values_from ?? null) !== null,
  );
}
